// The cat's Markov policy: how long it stays in a settled pose, what it picks when it moves on, which loop it plays meanwhile —
// all as weights from its needs (energy E, boredom B, annoyance A, 0–100). Used by the brain, and inlined as-is into the
// policy viewer (scripts/markov.mjs), so this file imports nothing.

const clampP = (v, a, b) => Math.max(a, Math.min(b, v));

// Activity: movement choices weigh this many times more, and settled poses are left this many times sooner (1 = normal cat;
// higher while debugging, so it keeps moving). Set by the app (tray menu).
let activity = 1;
export const ACTIVE_MODE = 5;
export function setActivity(a) {
  activity = a;
}
export const getActivity = () => activity;
const MOVES = new Set(['wander', 'stare', 'run', 'jump', 'climb']);
const SETTLES = new Set(['idle', 'sit', 'lie', 'curl', 'back']); // (getting up and waking are neither: they lead on to moving)

/** Expected stay in a settled pose (seconds); each loop end leaves with probability 1 - exp(-loop length / stay). */
export function stayTime(pose, needs) {
  const t = baseStay(pose, needs);
  return t && Math.max(4, t / activity);
}
function baseStay(pose, { energy: E, boredom: B }) {
  if (pose === 'curl') return E < 85 ? 300 + (85 - E) * 20 : 60; // sleeps until rested
  if (pose === 'lie') return clampP(150 - B + (60 - E), 40, 300);
  if (pose === 'back') return 15;
  if (pose === 'sit') return clampP(45 - 0.3 * B + (E < 30 ? 30 : 0), 12, 70);
  return 0;
}

/** What the cat may do next from a pose: [choice, weight]. Settled poses: what it does when it leaves. */
export function choiceWeights(pose, needs) {
  return baseChoices(pose, needs).map(([c, w]) => [c, MOVES.has(c) ? w * activity : SETTLES.has(c) ? w / activity : w]);
}
function baseChoices(pose, { energy: E, boredom: B }) {
  switch (pose) {
    case 'sit':
      return [
        ['stand', 1 + B / 60],
        ['curl', (100 - E) / 20],
        ['lie', (100 - E) / 40],
        ['back', 0.2 + B / 200],
        ['wander', (B / 30) * (E / 60)],
      ];
    case 'lie':
      return [['stand', 1]];
    case 'curl':
      return [['wake', 1]];
    case 'back':
      return [['roll', 1]];
    case 'stand':
      return [
        ['idle', 1.2],
        ['sit', 0.7 + (100 - E) / 60],
        ['lie', 0.4 + (100 - E) / 40],
        ['stare', 0.6 + B / 25],
        ['wander', (B / 25) * (E / 60)],
      ];
    case 'stand_stare':
      return [
        ['stand', 0.8],
        ['run', (E / 25) * (B / 15)],
        ['jump', (E / 25) * (B / 15)],
        ['climb', (E / 30) * (B / 15)],
        ['wander', B / 50],
      ];
  }
  return [];
}

/** Loops played while staying in a pose: [clip name or { hold: [min s, max s] }, weight]. */
export function loopWeights(pose, { energy: E }) {
  switch (pose) {
    case 'stand':
      return [[{ hold: [1.5, 4] }, 1.2], ['stand__idle__stand', 1], ['stand__shout__stand', 0.08]];
    case 'sit':
      return [[{ hold: [3, 8] }, 3], ['sit__yawn__sit', 0.4 * (1 + (100 - E) / 50)]];
    case 'lie':
      return [['lie__idle__lie', 3], ['lie__groom__lie', 0.6], ['lie__stretch__lie', 0.5], ['lie__swap__lie', 0.5], [{ hold: [4, 10] }, 1]];
    case 'curl':
      return [['curl__sleep__curl', 3], ['curl__sleep_bubble__curl', 1]];
    case 'back':
      return [['back__cute__back', 1], [{ hold: [2, 4] }, 0.5]];
  }
  return [[{ hold: [1, 2] }, 1]];
}

/** Where the cat ends a wander (walk) and how often. */
export const WANDER_ENDS = [['stand', 0.5], ['sit', 0.35], ['stand_stare', 0.15]];

/** Needs change per second by what the cat is doing. */
export const NEEDS = {
  sleep: { energy: 0.12, boredom: -0.02 },
  rest: { energy: -0.008, boredom: 0.07 },
  idle: { energy: -0.02, boredom: 0.12 },
  walk: { energy: -0.06, boredom: -0.35 },
  run: { energy: -0.25, boredom: -0.8 },
  climb: { energy: -0.3, boredom: -0.6 },
  jump: { energy: -0.3, boredom: -1 },
  held: { energy: -0.1, boredom: -0.5, annoyance: 3 },
};

/** How a need changes per second doing something (activity slows tiring, so an active cat doesn't just sleep it off). */
export function needDelta(tag, need) {
  const d = (NEEDS[tag] ?? NEEDS.idle)[need] ?? 0;
  return need === 'energy' && d < 0 ? d / activity : d;
}

// Jumping and climbing limits, in cat heights (H)
export const JUMP_MAX = 8; // horizontal
export const JUMP_UP = 6.5; // about half a screen
export const JUMP_DOWN = 8;
export const JUMP_FAIL_EXTRA = 1.2; // beyond the limits by up to this (0.8 up): still tries, and misses
export const SMALL_JUMP = { dx: 1.6, dy: 0.8 };
export const WALK_MIN = 2; // a move shorter than this (H) is a hop, not a walk (a short walk would look like walking on the spot)
export const CLIMB_REACH = 1; // a window side may start this far above the cat's platform (it reaches up and grabs it)
export const SHORT_FALL = 3.5; // falls from a wall shorter than this use the short fall clip
export const FALL_FAR = 4; // a drop from this high (H) loops the falling clip before the landing; lower ones use the landing clip alone
export const FALL_BRACE_FRAMES = 9; // on a long drop, only the last air frames of the landing clip (bracing for the ground) are played
